export const TRANSPORT_LABELS = {
  sms: 'SMS',
  email: 'Email',
  push: 'App',
};

const TRANSPORT_TYPES = ['sms', 'email', 'push'];

const STATUS_MAPPINGS = {
  sms: {
    success: ['delivered'],
    pending: ['sent'],
    error: ['failed', 'undelivered'],
    unsubscribed: ['unsubscribed'],
  },
  email: {
    success: ['delivered'],
    pending: ['accepted'],
    error: ['temporary_fail', 'permanent_fail', 'failed', 'rejected'],
    unsubscribed: [],
  },
  push: {
    success: ['ok', 'delivered'],
    pending: ['pending'],
    error: ['error'],
    unsubscribed: [],
  },
};

const UI_STATUS_TIER = {
  success: 3,
  error: 2,
  unsubscribed: 1,
  pending: 0,
};

export function mapTransportStatus(transportType, rawStatus) {
  const mapping = STATUS_MAPPINGS[transportType] || STATUS_MAPPINGS.push;
  if (mapping.success.includes(rawStatus)) {
    return 'success';
  }
  if (mapping.pending.includes(rawStatus)) {
    return 'pending';
  }
  if (mapping.unsubscribed.includes(rawStatus)) {
    return 'unsubscribed';
  }
  if (mapping.error.includes(rawStatus)) {
    return 'error';
  }
  return 'pending';
}

export function pickMostImportantNotificationStatus(transportType, statuses) {
  if (!statuses?.length) {
    return null;
  }

  let best = null;
  let bestTier = -1;

  statuses.forEach((entry) => {
    const tier = UI_STATUS_TIER[mapTransportStatus(transportType, entry.status)];
    const occurredAt = entry.occurred_at ?? 0;

    if (
      tier > bestTier ||
      (tier === bestTier && occurredAt > (best?.occurred_at ?? 0))
    ) {
      best = entry;
      bestTier = tier;
    }
  });

  return best;
}

export function getTransportStatuses(user = {}) {
  const settings = user.user_notification_transport_settings?.[0] || {};

  return TRANSPORT_TYPES.map((transportType) => {
    const isEnabled = settings[transportType] ?? false;
    const statusArray = user[`${transportType}_statuses`] || [];
    const latestStatus = statusArray[0]?.status || null;
    const messageCount = statusArray.length;

    let misconfigured = false;
    if (transportType === 'push' && isEnabled) {
      misconfigured = !user.push_notification_tokens || user.push_notification_tokens.length === 0;
    }

    return {
      type: transportType,
      enabled: isEnabled,
      messageStatus: latestStatus,
      messageCount,
      misconfigured,
    };
  });
}

export function resolveTransportBadge(transport) {
  const { type, enabled, messageStatus, messageCount, misconfigured } = transport;
  const transportLabel = TRANSPORT_LABELS[type];

  if (!enabled) {
    return {
      variant: 'disabled',
      icon: '○',
      tooltip: `${transportLabel} notifications are disabled`,
    };
  }

  if (misconfigured || messageStatus === 'error' || messageStatus === 'unsubscribed') {
    if (misconfigured && type === 'push') {
      return {
        variant: 'error',
        icon: '✗',
        tooltip: `${transportLabel}: enabled and needs in-app set up`,
      };
    } else if (misconfigured) {
      return {
        variant: 'error',
        icon: '✗',
        tooltip: `${transportLabel}: enabled but misconfigured`,
      };
    }
    if (messageStatus === 'unsubscribed') {
      return {
        variant: 'error',
        icon: '✗',
        tooltip: `${transportLabel}: enabled but user has unsubscribed`,
      };
    }
    return {
      variant: 'error',
      icon: '✗',
      tooltip: `${transportLabel}: enabled but last message failed`,
    };
  }

  if (messageCount > 0 && messageStatus === 'pending') {
    return {
      variant: 'warning',
      icon: '◐',
      tooltip: `${transportLabel}: enabled and pending verification`,
    };
  }

  if (messageCount > 0 && messageStatus === 'delivered') {
    return {
      variant: 'success',
      icon: '●',
      tooltip: `${transportLabel}: enabled and verified`,
    };
  }

  return {
    variant: 'warning',
    icon: '⚠',
    tooltip: `${transportLabel}: not verified. View user to send verification message`,
  };
}
